import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Skeleton,
} from '@mui/material';
import { SimpleSelect } from 'components/CustomSelectFields';
import { getBatchUsage } from '../../services/analyticsV2';
import { DurationOptions, InterviewVariantTypes } from './constant';

const Usage = ({ batchId, interviewVariantType = InterviewVariantTypes.VERBAL_MOCK }) => {
  const [duration, setDuration] = useState(DurationOptions.find(opt => opt._id === DurationOptions[4]._id) || 'last_3_months');
  const [usageData, setUsageData] = useState({ finishedAttempts: 0, unfinishedAttempts: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        setLoading(true);
        const response = await getBatchUsage(batchId, interviewVariantType, duration._id);
        setUsageData(response || { finishedAttempts: 0, unfinishedAttempts: 0 });
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching usage data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (batchId) {
      fetchUsageData();
    }
  }, [batchId, interviewVariantType, duration]);

  const handleDurationChange = (e) => {
    const duration = e.target.value;
    setDuration(DurationOptions.find(opt => opt._id === duration));
  };

  const UsageSkeleton = () => (
    <Box display="flex" flexDirection="column" gap="12px" width="50%">
      <Typography variant="h4-medium">Usage</Typography>

      <SimpleSelect
        options={DurationOptions}
        value={duration._id}
        onSelect={handleDurationChange}
        sx={{ width: '100%' }}
        label='Duration'
      />
      <Box display='flex' flexDirection='column' gap={4} mt={1}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: '8px',
            p: 4,
            boxShadow: '0px 0px 3px 0px #00000080',
          }}
        >
          <Box>
            <Typography variant='h6-semiBold' color='success.clr-500'>
              Finished attempts
            </Typography>
            <Skeleton variant="text" width={80} height={40} />
          </Box>
          <img
            src="https://assets.languify.in/images/usageF.png"
            alt="Finished"
            width={60}
            height={60}
          />
        </Paper>

        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: '8px',
            p: 4,
            boxShadow: '0px 0px 3px 0px #00000080',
          }}
        >
          <Box>
            <Typography variant='h6-semiBold' color='danger.clr-500'>
              Unfinished attempts
            </Typography>
            <Skeleton variant="text" width={80} height={40} />
          </Box>
          <img
            src="https://assets.languify.in/images/usageC.png"
            alt="Unfinished"
            width={60}
            height={60}
          />
        </Paper>
      </Box>
    </Box>
  );

  if (loading) {
    return <UsageSkeleton />;
  }

  const UsageEmptyState = () => (
    <Box display="flex" flexDirection="column" gap="12px" width="50%">
      <Typography variant="h4-medium">Usage</Typography>

      <SimpleSelect
        options={DurationOptions}
        value={duration._id}
        onSelect={handleDurationChange}
        sx={{ width: '100%' }}
        label='Duration'
      />
      <Box display='flex' flexDirection='column' gap={4} mt={1}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: '8px',
            p: 4,
            boxShadow: '0px 0px 3px 0px #00000080',
          }}
        >
          <Box>
            <Typography variant='h6-semiBold' color='success.clr-500'>
              Finished attempts
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'neutral.clr-600' }}>
              N/A
            </Typography>
          </Box>
          <img
            src="https://assets.languify.in/images/usageF.png"
            alt="Finished"
            width={60}
            height={60}
            style={{ opacity: 0.5 }}
          />
        </Paper>

        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: '8px',
            p: 4,
            boxShadow: '0px 0px 3px 0px #00000080',
          }}
        >
          <Box>
            <Typography variant='h6-semiBold' color='danger.clr-500'>
              Unfinished attempts
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'neutral.clr-600' }}>
              N/A
            </Typography>
          </Box>
          <img
            src="https://assets.languify.in/images/usageC.png"
            alt="Unfinished"
            width={60}
            height={60}
            style={{ opacity: 0.5 }}
          />
        </Paper>
      </Box>
    </Box>
  );

  if (error) {
    return <UsageEmptyState />;
  }
  return (
    <Box display="flex" flexDirection="column" gap="12px" width="50%">
      <Typography variant="h4-medium">Usage</Typography>

      <SimpleSelect
        options={DurationOptions}
        value={duration._id}
        onSelect={handleDurationChange}
        sx={{ width: '100%' }}
        label='Duration'
      />
      <Box display='flex' flexDirection='column' gap={4} mt={1}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: '8px',
            p: 4,
            boxShadow: '0px 0px 3px 0px #00000080',
          }}
        >
          <Box>
            <Typography variant='h6-semiBold' color='success.clr-500'>
              Finished attempts
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {usageData.finishedAttempts || 0}
            </Typography>
          </Box>
          <img
            src="https://assets.languify.in/images/usageF.png"
            alt="Finished"
            width={60}
            height={60}
          />
        </Paper>

        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: '8px',
            p: 4,
            boxShadow: '0px 0px 3px 0px #00000080',
          }}
        >
          <Box>
            <Typography variant='h6-semiBold' color='danger.clr-500'>
              Unfinished attempts
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {usageData.unfinishedAttempts || 0}
            </Typography>
          </Box>
          <img
            src="https://assets.languify.in/images/usageC.png"
            alt="Unfinished"
            width={60}
            height={60}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default Usage;
